import { useState, type ReactNode } from "react";
import { useElementWidth } from "@/utils/useElementWidth";
import type { BoxStats } from "../evalDistributionModel";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { CHART_OUTLIER } from "./chartTokens";
import { MERGED_SERIES_KEY } from "./evalCompareModel";
import styles from "./BoxPlotChart.module.css";
import {
  MergedSeriesGradient,
  mergedSeriesPaint,
  useMergedSeriesGradientId,
} from "./MergedSeriesGradient";

const PAD = { top: 12, right: 12, bottom: 26, left: 38 };
const HEIGHT = 240;
const TICKS = 4;
// 단일 시리즈의 기본 스타일이다. run 색이 없을 때도 이 값을 쓴다. training-infra 의 teal 과 같다.
const BOX = {
  stroke: "#2dd4bf",
  strokeHover: "#5eead4",
  fill: "rgba(45,212,191,0.16)",
  fillHover: "rgba(45,212,191,0.28)",
} as const;

export type BoxGroup = {
  key: string;
  // 생략하면 단일 시리즈로 그린다. 이때 hover 라벨과 툴팁 제목을 표시하지 않는다.
  label?: string;
  stats: BoxStats;
  color?: string;
};

export const boxPlotGridClass = styles.grid;

function domainOf(groups: readonly BoxGroup[]): [number, number] {
  const values = groups.flatMap((group) => [
    group.stats.min,
    group.stats.max,
    ...group.stats.outliers,
  ]);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const pad = (high - low) * 0.08 || 1;
  return [low - pad, high + pad];
}

function decimalsFor(low: number, high: number): number {
  const range = Math.abs(high - low);
  if (!Number.isFinite(range) || range === 0) return 1;
  if (range < 1) return 2;
  if (range < 10) return 1;
  return 0;
}

export function BoxPlotChart({
  title,
  groups,
  children,
}: {
  title: string;
  groups: readonly BoxGroup[];
  children?: ReactNode;
}): ReactNode {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const [hovered, setHovered] = useState<number | null>(null);
  const mergedGradientId = useMergedSeriesGradientId();

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const [low, high] = groups.length > 0 ? domainOf(groups) : [0, 1];
  const y = (value: number) => PAD.top + plotH - ((value - low) / (high - low)) * plotH;
  const slot = groups.length > 0 ? plotW / groups.length : 0;
  const boxW = Math.min(56, slot * 0.5);
  const centerX = (index: number) => PAD.left + slot * (index + 0.5);
  const ticks = Array.from(
    { length: TICKS + 1 },
    (_, index) => low + ((high - low) * index) / TICKS,
  );
  const decimals = decimalsFor(low, high);
  const format = (value: number) => value.toFixed(decimals);
  const hoveredGroup = hovered === null ? null : groups[hovered];
  const active = hoveredGroup?.stats ?? null;
  const tooltipTop =
    active !== null ? Math.min(y(active.max), ...active.outliers.map((outlier) => y(outlier))) : 0;

  return (
    <div ref={ref} className={styles.panel}>
      <h4 className={styles.panelTitle}>{title}</h4>
      {width > 0 && groups.length > 0 ? (
        <div className={styles.plotArea}>
          <svg width={width} height={HEIGHT} role="img" aria-label={title}>
            <defs>
              <MergedSeriesGradient id={mergedGradientId} />
            </defs>
            {ticks.map((tick, index) => (
              <g key={index}>
                <line
                  x1={PAD.left}
                  x2={width - PAD.right}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke={CHART_CHROME.grid}
                />
                <text
                  x={PAD.left - 6}
                  y={y(tick)}
                  dy="0.32em"
                  textAnchor="end"
                  fontSize={11}
                  className={styles.tickText}
                >
                  {tick.toFixed(decimals)}
                </text>
              </g>
            ))}

            {groups.map((group, index) => {
              const cx = centerX(index);
              const stats = group.stats;
              const isHover = hovered === index;
              const seriesColor =
                group.color === undefined
                  ? undefined
                  : mergedSeriesPaint(group.key, group.color, mergedGradientId);
              const stroke = seriesColor ?? (isHover ? BOX.strokeHover : BOX.stroke);
              const medianStroke = group.color ?? BOX.strokeHover;
              const boxFill = seriesColor ?? (isHover ? BOX.fillHover : BOX.fill);
              const isMerged = group.key === MERGED_SERIES_KEY;
              const idleOpacity = isMerged ? 0.72 : 0.18;
              const hoverOpacity = isMerged ? 0.88 : 0.32;
              const boxFillOpacity =
                seriesColor === undefined ? undefined : isHover ? hoverOpacity : idleOpacity;
              return (
                <g
                  key={group.key}
                  role="img"
                  aria-label={`${group.label === undefined ? "" : `${group.label}: `}median ${format(stats.median)}, Q1 ${format(stats.q1)}, Q3 ${format(stats.q3)}`}
                  onMouseEnter={() => setHovered(index)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <rect
                    x={cx - slot / 2}
                    y={PAD.top}
                    width={slot}
                    height={plotH}
                    fill="transparent"
                  />
                  <line
                    x1={cx}
                    x2={cx}
                    y1={y(stats.max)}
                    y2={y(stats.min)}
                    stroke={CHART_CHROME.axis}
                  />
                  <line
                    x1={cx - boxW / 4}
                    x2={cx + boxW / 4}
                    y1={y(stats.max)}
                    y2={y(stats.max)}
                    stroke={CHART_CHROME.axis}
                  />
                  <line
                    x1={cx - boxW / 4}
                    x2={cx + boxW / 4}
                    y1={y(stats.min)}
                    y2={y(stats.min)}
                    stroke={CHART_CHROME.axis}
                  />
                  <rect
                    x={cx - boxW / 2}
                    y={y(stats.q3)}
                    width={boxW}
                    height={Math.max(1, y(stats.q1) - y(stats.q3))}
                    rx={2}
                    fill={boxFill}
                    fillOpacity={boxFillOpacity}
                    stroke={stroke}
                  />
                  <line
                    x1={cx - boxW / 2}
                    x2={cx + boxW / 2}
                    y1={y(stats.median)}
                    y2={y(stats.median)}
                    stroke={medianStroke}
                    strokeWidth={2}
                  />
                  {stats.outliers.map((outlier, outlierIndex) => (
                    <circle
                      key={outlierIndex}
                      cx={cx}
                      cy={y(outlier)}
                      r={2.5}
                      fill="none"
                      stroke={CHART_OUTLIER}
                    />
                  ))}
                  {isHover && group.label !== undefined ? (
                    <text
                      x={cx}
                      y={HEIGHT - 8}
                      textAnchor="middle"
                      fontSize={11}
                      className={styles.groupLabel}
                      fill={group.color ?? BOX.strokeHover}
                    >
                      {group.label}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </svg>

          {active !== null && hoveredGroup !== null && hovered !== null ? (
            <div className={styles.tooltip} style={{ left: centerX(hovered), top: tooltipTop - 8 }}>
              {hoveredGroup.label !== undefined ? (
                <p className={styles.tooltipTitle}>{hoveredGroup.label}</p>
              ) : null}
              <p className={styles.tooltipStats}>
                med <span className={styles.tooltipAccent}>{format(active.median)}</span> · IQR{" "}
                {format(active.q1)}–{format(active.q3)} · n={active.count}
              </p>
              {active.outliers.length > 0 ? (
                <p className={styles.tooltipOutliers}>이상치 {active.outliers.length}개</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}
